import { DOMEventHandlerMock, DOMSimpleEventHandlerMock } from "@ciaopeople/cp-shatsu/DOMEventHandlerMock";
import { BrowserLogger } from "@ciaopeople/cp-shatsu/Logging";
import { isDefined } from "@ciaopeople/cp-shatsu/TypeChecking";
import Semaphore from "semaphore-promise";
import { groupBy } from "../../../lib/utils/GroupBy.js";
import { VideoRef, MediaResourceStats } from "../../player/MediaResource.js";
import { VideoContext } from "../../player/Player.js";
import { WhenTimeOccurs, WhenTimeOccursPolicy } from "../../player/utils/WhenTimeOccurs.js";
import { AdBreakType } from "../AdUnit.js";
import { AdContainerControls } from "../AdsController.js";
import { AdsEnrichedVideoContext } from "../AdsEnrichedVideoContext.js";
import { ContentResumePolicy } from "../ContentResumePolicy.js";
import { AbstractImaController, AdResumableTriggerBehaviour } from "./AbstractImaController.js";
import { AdItemControls, AdItemStatus } from "./ImaAdItem.js";
import { AdPreloadConfig, VASTAdUnitConfig } from "./MultipleVASTImaAdCallController.js";
import { GroupSuccessCriteria, ItemPrepareStrategy, GroupedVASTImaAdItems, NullVASTImaAdItem, SingleVASTImaAdItem, VASTImaAdItem } from "./items/VASTImaAdItem.js";
import { MediaControlOrigin } from "../../events/PlayerEvent.js";
import { GenericFormatLabel, ParsedFormat } from "../../player/video/FormatAndSizePreference.js";


interface VASTagExecutionInfo {
    tag: VASTImaAdItem,
    resumePolicy: ContentResumePolicy,
    onAdStart?: () => void
    onAdEnd?: () => void
}

interface MidrollTagExecutionInfo extends VASTagExecutionInfo {
    time: number
}


type ResumeAndGroupPolicy = {
    resume: ContentResumePolicy,
    group_success: GroupSuccessCriteria
}

export const INDIRECT_EVENTS_MAP = { "ended": true };


export function buildImaAdItem(units: VASTAdUnitConfig[], resource: VideoRef, ima: AbstractImaController,
    successCriteria: GroupSuccessCriteria = GroupSuccessCriteria.ALL): VASTImaAdItem {
    let retval: VASTImaAdItem;
    if (units && units.length > 0) {
        const items = units.map((e, idx) => new SingleVASTImaAdItem(e.unit, resource, ima, idx == 0));
        if (items.length > 1) {
            retval = new GroupedVASTImaAdItems(items, ItemPrepareStrategy.ALAP, successCriteria);
        }
        else retval = items.shift();
    }
    return retval;
}

export function filterAndLinearizeMidrolls(midrolls: VASTAdUnitConfig[], controls: VideoContext, imaController: AbstractImaController, videoDuration: number, rAndG: ResumeAndGroupPolicy): MidrollTagExecutionInfo[] {
    const mappedMidrolls: Map<number, VASTAdUnitConfig[]> = groupBy<number, VASTAdUnitConfig>(midrolls.filter(cfg => {
        return isDefined(cfg.midrollTime) &&
            ((cfg.midrollTime.expressionType == "specific-time" &&
                cfg.midrollTime.value < videoDuration) ||
                (cfg.midrollTime.expressionType == "percentage"))
    }).map(cfg => {
        const linearizedTime = Math.floor(cfg.midrollTime.expressionType == "percentage" ? videoDuration * cfg.midrollTime.value / 100 : cfg.midrollTime.value);
        return [linearizedTime, cfg];
    }))
    const retval: MidrollTagExecutionInfo[] = [];
    mappedMidrolls.forEach((v, time) => {
        const item = buildImaAdItem(v, controls.ref, imaController, rAndG.group_success);
        const lastCfg = v.slice(-1).pop();
        const rPolicy = lastCfg ? lastCfg.contentResumePolicy : rAndG.resume;
        retval.push({
            tag: item,
            resumePolicy: rPolicy,
            time: time
        });
    })

    return retval;

}


function splitTagsByBreakType(tags: VASTAdUnitConfig[]): Map<AdBreakType, VASTAdUnitConfig[]> {
    return groupBy<AdBreakType, VASTAdUnitConfig>(tags.map(e => [e.unit.breakType, e]));
}

const log = new BrowserLogger("AD-EVT", "#8A2BE2");

export type ResumeAndGroupPolicyMap = { [k in AdBreakType]: ResumeAndGroupPolicy };

export const DefaultRAGPolicyMap: ResumeAndGroupPolicyMap = {
    preroll: {
        resume: ContentResumePolicy.RESUME_ALWAYS,
        group_success: GroupSuccessCriteria.AT_LEAST_ONE
    },
    midroll: {
        resume: ContentResumePolicy.RESUME_ALWAYS,
        group_success: GroupSuccessCriteria.AT_LEAST_ONE
    },
    postroll: {
        resume: ContentResumePolicy.NEVER_RESUME,
        group_success: GroupSuccessCriteria.AT_LEAST_ONE
    }
}

export class MultipleTagsImaEnrichedControls implements AdsEnrichedVideoContext {

    public readonly isAdsEnriched: boolean = true;
    private alreadyStarted: boolean;

    private internalEvents: DOMEventHandlerMock;
    protected onlyPlayingTimeOccurs: WhenTimeOccurs;
    protected timeOccurs: WhenTimeOccurs;
    private preroll: VASTagExecutionInfo;
    private postroll: VASTagExecutionInfo;
    private midrolls: MidrollTagExecutionInfo[];



    private _isAdPlaying: boolean;
    private _currentAd: Promise<AdItemControls>;
    private _currentNonLinearBackground: Promise<AdItemControls>;
    private itemExecutionQueue: VASTagExecutionInfo[];
    private queueSemaphore: Semaphore;



    constructor(
        protected videoContext: VideoContext,
        protected containerControls: AdContainerControls,
        protected imaController: AbstractImaController,
        private preloadConfig: AdPreloadConfig,
        units: VASTAdUnitConfig[],
        private allowNonLinearBackground: boolean = false,
        private adResumableTriggerBehaviour: AdResumableTriggerBehaviour = AdResumableTriggerBehaviour.REGULAR,
        private policies: ResumeAndGroupPolicyMap = DefaultRAGPolicyMap
    ) {


        this._isAdPlaying = false;
        this.itemExecutionQueue = [];
        this.queueSemaphore = new Semaphore();
        this._currentAd = null;
        this._currentNonLinearBackground = null;


        this.alreadyStarted = false;
        this.onlyPlayingTimeOccurs = new WhenTimeOccurs(videoContext, WhenTimeOccursPolicy.ON_PLAYING);
        this.timeOccurs = new WhenTimeOccurs(videoContext, WhenTimeOccursPolicy.ALWAYS);
        const mapped = splitTagsByBreakType(units);
        this.preroll = this.buildProperItem(mapped, AdBreakType.PREROLL);
        this.postroll = this.buildProperItem(mapped, AdBreakType.POSTROLL);
        this.internalEvents = new DOMSimpleEventHandlerMock();
        this.resumableTriggerFirstPart();
        console.log("PReload config", this.preloadConfig)
        if (this.preloadConfig.enable && !this.preloadConfig.excludePreloadOnPreroll) this.preroll.tag.load();
        console.log("VIDEO READY STATE: " + this.videoContext.ref.element.readyState);

        this.videoContext.metadataPromise.then(() => {

            const videoDuration = this.videoContext.duration;
            const midrolls = mapped.has(AdBreakType.MIDROLL) ? mapped.get(AdBreakType.MIDROLL) : [];

            this.prepareMidrolls(midrolls, videoDuration);

            this.resumableTriggerSecondPart();

            if (this.preloadConfig.enable) {
                const pTime = videoDuration - this.preloadConfig.time;
                this.timeOccurs.whenTimeIsAfter(pTime, () => {
                    log.info("Preloading postroll at", pTime, this.videoContext.currentTime);
                    this.postroll.tag.load();
                })
            }

        });


        this.hijackEndedEventForPostroll();

    }


    destroy(): void {
        this.onlyPlayingTimeOccurs.destroy();
        this.timeOccurs.destroy();
        this.videoContext.off("ended", this.triggerEnded);
        this.getCurrentAd().then(coup => coup[1]).then(ad => ad.destroy());
    }

    private triggerEnded(e: Event) {
        log.info("EVENT Ended triggered");
        this.internalEvents?.trigger("ended", e);
    }


    private hijackEndedEventForPostroll() {
        this.videoContext.once("ended", (evt: Event) => {
            const originalEvent = evt;
            const existingAction = this.postroll.onAdEnd;
            this.postroll.onAdEnd = () => {
                log.debug("Hi jacking ended event");
                if (existingAction) existingAction();
                this.triggerEnded(originalEvent);
                this.videoContext.on("ended", this.triggerEnded);
            };
            this.startItem(this.postroll);

        });
    }


    private prepareMidrolls(midrollsConfig: VASTAdUnitConfig[], videoDuration: number): void {
        if (midrollsConfig && midrollsConfig.length > 0) {
            this.midrolls = filterAndLinearizeMidrolls(midrollsConfig, this.videoContext, this.imaController,
                videoDuration, this.policies.midroll);
            this.midrolls.forEach(mid => {
                this.onlyPlayingTimeOccurs.whenTimeIsAfter(mid.time, () => {
                    log.info("Starting Midroll at", mid.time, this.videoContext.currentTime);
                    this.startItem(mid)
                });
                if (this.preloadConfig.enable) {
                    this.timeOccurs.whenTimeIsAfter(mid.time - this.preloadConfig.time, () => {
                        log.info("Preloading midroll at", mid.time, this.videoContext.currentTime);
                        mid.tag.load()
                    });
                }
            })
        }
    }



    private resumableTriggerFirstPart() {
        if (this.adResumableTriggerBehaviour == AdResumableTriggerBehaviour.DEFER_PREROLL_TRIGGER_TO_FIRST_MIDROLL &&
            (!this.midrolls || this.midrolls.length <= 0)) {
            log.debug("Stepping back AdResumableTriggerBehaviour to REGULAR; no midrolls");
            this.adResumableTriggerBehaviour = AdResumableTriggerBehaviour.REGULAR;
        }

        if (!this.postroll.tag.isEmpty) {
            this.postroll.onAdStart = () => {
                this.triggerAdPausedMedia(this.postroll.tag);
            }


        }
        this.postroll.onAdEnd = () => {
            this.triggerAdResumableMedia(this.postroll.tag);
        }
        if (!this.preroll.tag.isEmpty) {
            this.preroll.onAdStart = () => {
                this.triggerAdPausedMedia(this.preroll.tag);
            }

        }
        this.preroll.onAdEnd = () => {
            if (this.adResumableTriggerBehaviour == AdResumableTriggerBehaviour.REGULAR) {
                this.triggerAdResumableMedia(this.preroll.tag);
            }
        }

    }

    private resumableTriggerSecondPart() {

        if (this.midrolls && this.midrolls.length > 0) {
            this.midrolls.forEach(mid => {
                mid.onAdStart = () => {
                    this.triggerAdPausedMedia(mid.tag);
                }
                mid.onAdEnd = () => {
                    this.triggerAdResumableMedia(mid.tag);
                }
            });
        }

    }


    private triggerAdResumableMedia(item: VASTImaAdItem): void {
        const units = item.units;
        const unit = units[units.length - 1];
        this.imaController.signalResumableMedia(unit, unit, this.videoContext.ref.info, this.videoContext.ref.executionId);
    }

    private triggerAdPausedMedia(item: VASTImaAdItem): void {
        const units = item.units;
        const unit = units[units.length - 1];
        this.imaController.signalPausedMedia(unit, unit, this.videoContext.ref.info, this.videoContext.ref.executionId);
    }



    private buildProperItem(mapped: Map<AdBreakType, VASTAdUnitConfig[]>, bType: AdBreakType): VASTagExecutionInfo {
        const units = mapped.has(bType) ? mapped.get(bType) : [];
        const defpol = this.policies[bType].resume;
        const bItem = buildImaAdItem(units, this.videoContext.ref, this.imaController, this.policies[bType].group_success);
        if (bItem) {
            const last = units.slice(-1).pop();
            return {
                tag: bItem,
                resumePolicy: last ? last.contentResumePolicy : defpol
            }
        }
        else return {
            tag: new NullVASTImaAdItem(bType, bType),
            resumePolicy: defpol
        }

    }


    private startItem(xc: VASTagExecutionInfo): void {
        this.queueSemaphore.acquire("startItem").then(release => {
            log.debug("Start Item Acquire", xc.tag);
            this.itemExecutionQueue.push({
                ...xc
            });
            release();
            return;
        }).then(_ => this.dequeueIfPossible());
    }

    private dequeueIfPossible(): void {
        this.queueSemaphore.acquire("dequeueItem").then(release => {
            log.debug("Dequeue Acquire");

            if (!this._currentAd) {

                const extracted = this.itemExecutionQueue.shift();
                if (extracted) {
                    const copied: VASTagExecutionInfo = {
                        tag: extracted.tag,
                        resumePolicy: extracted.resumePolicy,
                        onAdEnd: extracted.onAdEnd,
                        onAdStart: extracted.onAdStart
                    }
                    const isBackgroundEligible = this.allowNonLinearBackground &&
                        !extracted.tag.isGroup &&
                        extracted.tag.units.pop().breakType == AdBreakType.MIDROLL;
                    this._isAdPlaying = true;
                    // this.containerControls.signalAdWaiting(true);
                    this._currentAd = extracted.tag.start().then(ctl => {
                        return ctl.adInfo().then(info => {
                            // this.containerControls.signalAdWaiting(false);
                            log.debug("GOT INFO", info);
                            const runbkg = info && !info.isLinear() && isBackgroundEligible;
                            if (copied.onAdStart && info != null) copied.onAdStart();
                            const maybePause = runbkg ? Promise.resolve() : this.videoContext.pause(MediaControlOrigin.ADV);
                            if (runbkg) {
                                log.debug("Running non linear in background");
                                this._currentNonLinearBackground = Promise.resolve(ctl);
                                copied.resumePolicy = ContentResumePolicy.RESUME_ALWAYS;

                            }

                            return maybePause.then(_ => {
                                //log.debug("Giving back controls")
                                return ctl
                            });

                        });
                    });
                    this._currentAd.then(ctl => ctl.completionPromise.then(res => {
                        this.signalItemEnd(copied, res);
                        //this.containerControls.signalAdWaiting(false);
                        if (copied.onAdEnd) copied.onAdEnd();
                    }))

                }
            }

            if (this._currentNonLinearBackground) {

                this._currentNonLinearBackground.then(e => e.close());
            }
            release();


        });

    }


    private signalItemEnd(item: VASTagExecutionInfo, endstatus: AdItemStatus): void {
        this.queueSemaphore.acquire("itemEnd").then(release => {
            log.debug("item end acquire");
            this._currentAd.then(e => e.destroy());
            this._currentAd = null;
            this._currentNonLinearBackground = null;
            this._isAdPlaying = false;
            this.containerControls.hide();
            if (item.resumePolicy == ContentResumePolicy.RESUME_ALWAYS ||
                item.resumePolicy == ContentResumePolicy.RESUME_ON_SUCCESS && endstatus == AdItemStatus.ENDED) {
                log.debug("playing content");
                this.videoContext.play(MediaControlOrigin.ADV).then(_ => release());
            }
            else release();
            return;
        }).then(_ => this.dequeueIfPossible());
    }



    protected getCurrentAd(): Promise<[boolean, Promise<AdItemControls>]> {
        return this.queueSemaphore.acquire("getItem").then(release => {
            const x: [boolean, Promise<AdItemControls>] = [this._isAdPlaying, this._currentAd];
            release();
            return x;
        });

    }


    startWithAds(forceResume: boolean): Promise<boolean> {
        if (!this.alreadyStarted) {
            this.alreadyStarted = true;

            this.startItem({
                ...this.preroll,
                resumePolicy: forceResume ? ContentResumePolicy.RESUME_ALWAYS : this.preroll.resumePolicy
            });
        }
        return Promise.resolve(true);
    }
    skipAds(): Promise<boolean> {
        return Promise.resolve(false);
    }
    pause(): Promise<void> {
        return this.getCurrentAd().then(c => {
            const [isPlaying, adctl] = c;
            if (isPlaying && adctl) {
                return adctl.then(ctl => ctl.pause());
            }
            else
                return this.videoContext.pause(MediaControlOrigin.USER);
        })

    }


    get ended(): boolean {
        return this.videoContext.ended
    }
    toggleFullscreen(fullscreen: boolean): boolean {
        return this.videoContext.toggleFullscreen(fullscreen)
    }
    reset(): Promise<any> {
        return this.videoContext.reset()
    }
    getCurrentFormat(): ParsedFormat {
        return this.videoContext.getCurrentFormat()
    }
    getAvailableQualities(): ParsedFormat[] {
        return this.videoContext.getAvailableQualities();
    }
    get stats(): MediaResourceStats {
        return this.videoContext.stats
    }
    get currentTime(): number {
        return this.videoContext.currentTime
    }
    get duration(): number {
        return this.videoContext.duration
    }
    get metadataPromise(): Promise<void> {
        return this.videoContext.metadataPromise
    }
    get isPaused(): boolean {
        return this.videoContext.isPaused
    }
    beforeUnload(): Promise<void> {
        return this.videoContext.beforeUnload();
    }
    autoplay(autoplay?: boolean): boolean {
        return this.videoContext.autoplay(autoplay)
    }
    show(): void {
        return this.videoContext.show()
    }
    fetchLastMediaControlOrigin(): MediaControlOrigin {
        return this.videoContext.fetchLastMediaControlOrigin()
    }
    restart(): void {
        return this.videoContext.restart();
    }
    reloadSource(keepCurrentTime: boolean): Promise<void> {
        return this.videoContext.reloadSource(keepCurrentTime)
    }

    play(origin: MediaControlOrigin): Promise<any> {
        return this.getCurrentAd().then(c => {
            const [isPlaying, adctl] = c;
            if (!isPlaying && adctl) {
                return adctl.then(ctl => ctl.togglePlay());
            }
            else
                return this.videoContext.play(origin);
        })
    }


    get ref(): VideoRef {
        return this.videoContext.ref;
    }

    togglePlay(manualPlay: boolean): Promise<boolean> {
        if (this.alreadyStarted) {
            return this.getCurrentAd().then(c => {
                const [isPlaying, adctl] = c;
                if (isPlaying && adctl) {
                    return adctl.then(ctl => ctl.togglePlay());
                }
                else {
                    return this.videoContext.togglePlay(manualPlay);
                }
            });
        }
        else return this.startWithAds(false);
    }

    toggleAudio(): Promise<boolean> {
        return Promise.all(
            [this.videoContext.toggleAudio(),
            this.getCurrentAd().then(c => {
                const [isPlaying, adctl] = c;
                if (adctl) return adctl.then(ct => ct.toggleAudio());
                else return Promise.resolve(true);
            })
            ]
        ).then(r => r[0]);
    }

    seek(time: number): Promise<boolean> {
        return this.getCurrentAd().then(c => {
            const isPlaying = c[0];
            if (!isPlaying) return this.videoContext.seek(time);
            else return Promise.reject("Ad is playing");
        })

    }

    switchQuality(format: GenericFormatLabel): Promise<ParsedFormat> {
        return this.videoContext.switchQuality(format);
    }


    volume(volume?: number): number {
        Promise.all(
            [Promise.resolve(this.videoContext.volume(volume)),
            this.getCurrentAd().then(c => {
                const [isPlaying, adctl] = c;
                if (adctl) return adctl.then(ct => ct.volume(volume));
                else return Promise.resolve(this.videoContext.volume())
            })
            ]
        )

        return this.videoContext.volume()
    }
    on(eventName: "progress" | "ended" | "timeupdate" | "pause" | "play" | "complete" | "waiting" | "stalled" | "volumechange" | "playing", listener: EventListener): void {
        if (eventName in INDIRECT_EVENTS_MAP) {
            this.internalEvents.on(eventName, listener);
        }
        else this.videoContext.on(eventName, listener);
    }
    off(eventName: "progress" | "ended" | "timeupdate" | "pause" | "play" | "complete" | "waiting" | "stalled" | "volumechange" | "playing", listener: EventListener): void {
        if (eventName in INDIRECT_EVENTS_MAP) {
            this.internalEvents.off(eventName, listener);
        }
        else this.videoContext.off(eventName, listener);
    }
    once(eventName: "progress" | "ended" | "timeupdate" | "pause" | "play" | "complete" | "waiting" | "stalled" | "volumechange" | "playing", listener: EventListener): void {
        if (eventName in INDIRECT_EVENTS_MAP) {
            this.internalEvents.one(eventName, listener);
        }
        else this.videoContext.once(eventName, listener);
    }
    unmute(): Promise<boolean> {
        return this.muted(false);
    }


    muted(enable?: boolean): Promise<boolean> {
        return Promise.all(
            [this.videoContext.muted(enable),
            this.getCurrentAd().then(c => {
                const [isPlaying, adctl] = c;
                if (adctl) return adctl.then(ct => ct.muted(enable));
                else return Promise.resolve(true);
            })
            ]
        ).then(r => r[0]);
    }



}
